import React from 'react';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { cn } from '@/utils/cn';
import { formatDateForInput, formatDateForDisplay } from '@/utils/format';

interface ScheduleStepProps {
  startDate: string;
  endDate: string;
  hasEndDate: boolean;
  onChangeStartDate: (date: string) => void;
  onChangeEndDate: (date: string) => void;
  onToggleEndDate: (enabled: boolean) => void;
  errors?: {
    startDate?: string;
    endDate?: string;
  };
}

export const ScheduleStep: React.FC<ScheduleStepProps> = ({
  startDate,
  endDate,
  hasEndDate,
  onChangeStartDate,
  onChangeEndDate,
  onToggleEndDate,
  errors,
}) => {
  const today = formatDateForInput(new Date());

  // Calculate minimum end date (must be after start date)
  const minEndDate = startDate || today;

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Campaign Schedule</h3>
        <p className="text-sm text-gray-600">
          Set when your campaign will start and optionally when it should end.
        </p>
      </div>

      {/* Start Date */}
      <div>
        <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-2">
          Start Date <span className="text-red-500">*</span>
        </label>
        <Input
          id="start-date"
          type="date"
          value={startDate}
          onChange={(e) => onChangeStartDate(e.target.value)}
          min={today}
          className={cn({
            'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.startDate,
          })}
          aria-invalid={!!errors?.startDate}
          aria-describedby="start-date-description start-date-error"
        />
        {errors?.startDate && (
          <p id="start-date-error" className="mt-1 text-sm text-red-600">
            {errors.startDate}
          </p>
        )}
        <p id="start-date-description" className="mt-1 text-xs text-gray-500">
          Your campaign will begin serving ads on this date. Defaults to today.
        </p>
      </div>

      {/* End Date Toggle */}
      <div className="p-4 border border-gray-200 rounded-lg">
        <label className="flex items-start cursor-pointer">
          <Checkbox
            checked={hasEndDate}
            onChange={(checked) => onToggleEndDate(checked)}
            aria-describedby="end-date-toggle-description"
          />
          <div className="ml-3 flex-1">
            <span className="block text-sm font-medium text-gray-900">Set an end date</span>
            <p id="end-date-toggle-description" className="text-xs text-gray-500 mt-1">
              Specify when your campaign should stop running. If not set, the campaign will run
              indefinitely (or until manually paused).
            </p>
          </div>
        </label>
      </div>

      {/* End Date Input */}
      {hasEndDate && (
        <div>
          <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-2">
            End Date <span className="text-red-500">*</span>
          </label>
          <Input
            id="end-date"
            type="date"
            value={endDate}
            onChange={(e) => onChangeEndDate(e.target.value)}
            min={minEndDate}
            className={cn({
              'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.endDate,
            })}
            aria-invalid={!!errors?.endDate}
            aria-describedby="end-date-description end-date-error"
          />
          {errors?.endDate && (
            <p id="end-date-error" className="mt-1 text-sm text-red-600">
              {errors.endDate}
            </p>
          )}
          <p id="end-date-description" className="mt-1 text-xs text-gray-500">
            Your campaign will stop serving ads at the end of this date.
          </p>
        </div>
      )}

      {/* Campaign Duration Info */}
      {startDate && hasEndDate && endDate && !errors?.endDate && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-700">
            <strong>Campaign Duration:</strong>{' '}
            {(() => {
              const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
              const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
              const start = new Date(startYear, startMonth - 1, startDay);
              const end = new Date(endYear, endMonth - 1, endDay);
              const diffTime = Math.abs(end.getTime() - start.getTime());
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
            })()}
            <span className="text-xs text-blue-600 block mt-1">
              From {formatDateForDisplay(startDate)} to{' '}
              {formatDateForDisplay(endDate)}
            </span>
          </p>
        </div>
      )}

      {!hasEndDate && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
          <p className="text-sm text-gray-600">
            Your campaign will run continuously from{' '}
            {startDate ? formatDateForDisplay(startDate) : 'the start date'}{' '}
            until you manually pause or remove it.
          </p>
        </div>
      )}
    </div>
  );
};
